// ──────────────────────────────────────────────────────
// HeroCard — premium recommendation card (role='hero').
// Two-column horizontal layout: image (~42% width) sits on the visual
// right (RTL flip), content column on the visual left with badge top,
// product name/brand/price, divider, hardcoded "ليش الأفضل:" header and
// up to 3 bullet reasons. Wrapped in a 2-layer animated ring: ::before
// is a sharp 2.5px border with a concentrated bright spot orbiting the
// card; ::after is a blurred outer bloom trailing the same spot for a
// magic-wand glow. Falls back to shortReasonAr when reasons[] is absent.
// ──────────────────────────────────────────────────────
import Link from 'next/link';
import type { ProductSuggestion } from '@/lib/types';
import { TrustBadge } from '@/components/TrustBadge';
import { isProductOutOfStock, OUT_OF_STOCK_LABEL_AR } from './availability-badge';

export function HeroCard({ product }: { product: ProductSuggestion }) {
    const ps = product;
    const bullets = (ps.reasons && ps.reasons.length > 0)
        ? ps.reasons.slice(0, 3)
        : (ps.shortReasonAr ? [ps.shortReasonAr] : []);
    const outOfStock = isProductOutOfStock(ps);

    return (
        <>
            {/* @property lets us animate the ANGLE of a conic-gradient
                (raw CSS vars can't be animated without typed registration).
                ::before = sharp 2.5px border ring with a concentrated bright
                spot orbiting the card. ::after = blurred outer bloom trailing
                the same spot. mask-composite:exclude keeps both gradients in
                their padding rings only; the Link's white fill stays
                untouched. The Link carries relative + z-10 so the ::after
                (z-index: -1) paints behind the card content, not on top.
                React 19 hoists inline <style> tags to <head>; duplicate
                identical rules are a no-op. */}
            <style>{`
                @property --shine-angle {
                    syntax: '<angle>';
                    initial-value: 0deg;
                    inherits: false;
                }
                .hero-shine-border {
                    position: relative;
                    border-radius: 16px;
                    padding: 2.5px;
                }
                .hero-shine-border::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    border-radius: 16px;
                    padding: 2.5px;
                    /* QAI design-system V4 re-skin (2026-05-03):
                       canonical accent shifted from V3 #1a838d to the
                       logo-derived #7ec2ce. Inner sharp ring stops:
                       #a8dce4 softer mint highlight, #7ec2ce canonical
                       accent, #c1edf2 slightly brighter pop at the
                       spot's center. Animation timing (3s linear
                       infinite) preserved from the legacy hero shine
                       — only the gradient stops changed. */
                    background: conic-gradient(
                        from var(--shine-angle, 0deg),
                        transparent 0%,
                        transparent 55%,
                        #a8dce4 62%,
                        #7ec2ce 70%,
                        #c1edf2 75%,
                        #7ec2ce 80%,
                        #a8dce4 88%,
                        transparent 95%,
                        transparent 100%
                    );
                    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
                    -webkit-mask-composite: xor;
                    mask-composite: exclude;
                    animation: hero-rotate-shine 3s linear infinite;
                    filter: blur(0.5px);
                    pointer-events: none;
                }
                .hero-shine-border::after {
                    content: '';
                    position: absolute;
                    inset: -3px;
                    border-radius: 19px;
                    padding: 5px;
                    /* Outer halo — same accent at lower opacities so the
                       glow trails the bright spot without overpowering
                       the dark page background. V4 accent #7ec2ce. */
                    background: conic-gradient(
                        from var(--shine-angle, 0deg),
                        transparent 0%,
                        transparent 60%,
                        rgba(126, 194, 206, 0.22) 68%,
                        rgba(126, 194, 206, 0.40) 75%,
                        rgba(126, 194, 206, 0.22) 82%,
                        transparent 90%,
                        transparent 100%
                    );
                    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
                    -webkit-mask-composite: xor;
                    mask-composite: exclude;
                    animation: hero-rotate-shine 3s linear infinite;
                    filter: blur(4px);
                    z-index: -1;
                    pointer-events: none;
                }
                @keyframes hero-rotate-shine {
                    0% { --shine-angle: 0deg; }
                    100% { --shine-angle: 360deg; }
                }
            `}</style>

            {/* QCARE-AI-DEV-FRONT-DARK-CARDS (2026-05-09): hero card
                shell switched from white to a dark navy/teal gradient so
                the card sits inside the dark QIO/QCare chat theme
                instead of fighting it. Hero shine ring + animation
                untouched. Image panel kept light so product photography
                still reads against teal accents. */}
            <div className="hero-shine-border rounded-2xl">
                <Link
                    href={ps.href}
                    className="relative z-10 block rounded-2xl overflow-hidden transition-shadow hover:shadow-lg cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                    style={{
                        background: 'linear-gradient(135deg, #0a1f24 0%, #0f2a30 50%, #0a1f24 100%)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)',
                        textDecoration: 'none',
                    }}
                >
                    {/* Two-column flex; RTL flips JSX-order so image sits visually on the RIGHT */}
                    <div className="flex w-full" style={{ direction: 'rtl', minHeight: 280 }}>

                        {/* Image column (~42%) — soft cream/cyan tint so
                            packaging photography stays legible on the dark
                            shell; thin teal-tint divider on its inner edge. */}
                        <div
                            className="flex-shrink-0 flex items-center justify-center"
                            style={{
                                width: '42%',
                                background: '#eaf3f5',
                                borderLeft: '1px solid rgba(126,194,206,0.18)',
                            }}
                        >
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                                src={ps.imageUrl}
                                alt={ps.nameAr}
                                className="w-full h-full object-contain p-4 q-card-image-fade"
                                style={{ maxHeight: 260 }}
                                // QCARE-AI-DEV-MOBILE-CHAT-ROUTINE-BLOCK-REVEAL-PHASE-1-5
                                // (2026-05-04): image fade-in on load via
                                // `.q-card-image-fade` (opacity 0 → 1, 380 ms).
                                // Reduced-motion skips the transition.
                                onLoad={(e) => {
                                    const el = e.target as HTMLImageElement;
                                    if (el.dataset.fallback === '1') return;
                                    el.style.opacity = '1';
                                }}
                                // External URLs occasionally 403/404/CORS-block
                                // (e.g. hotlink-protected pharmacy CDNs). Hide
                                // the broken icon and surface a neutral
                                // placeholder via inline replacement so the
                                // card's geometry stays unchanged.
                                onError={(e) => {
                                    const el = e.target as HTMLImageElement;
                                    if (el.dataset.fallback === '1') return;
                                    el.dataset.fallback = '1';
                                    el.src = '/branding/qio-logo.jpeg';
                                    el.style.opacity = '0.55';
                                }}
                            />
                        </div>

                        {/* Content column — badge top, product info middle, reasons bottom */}
                        <div className="flex-1 flex flex-col justify-between p-4 min-w-0">

                            {/* Top row: hero label (left, RTL-flipped) +
                                Phase 9S out-of-stock badge. Trust line was
                                duplicated here (TrustBadge) AND in the body —
                                R6L consolidates into a single bottom trust pill
                                so the header stays clean. */}
                            <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
                                {ps.label ? (
                                    <span
                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold"
                                        style={{
                                            background: 'linear-gradient(90deg, #0ea5a5 0%, #7ec2ce 100%)',
                                            color: '#ffffff',
                                            letterSpacing: '0.02em',
                                            boxShadow: '0 2px 8px rgba(126,194,206,0.30)',
                                        }}
                                    >
                                        {ps.icon && <span aria-hidden>{ps.icon}</span>}
                                        <span>{ps.label}</span>
                                    </span>
                                ) : <span />}
                                {outOfStock && (
                                    <span
                                        className="inline-flex items-center gap-1 rounded-md text-[10px] font-semibold"
                                        style={{
                                            background: 'rgba(180,35,24,0.15)',
                                            color: '#fca5a5',
                                            border: '1px solid rgba(252,165,165,0.40)',
                                            padding: '2px 6px',
                                        }}
                                        aria-label={OUT_OF_STOCK_LABEL_AR}
                                    >
                                        {OUT_OF_STOCK_LABEL_AR}
                                    </span>
                                )}
                            </div>

                            <div className="flex-1">
                                {/* Product name — bright teal anchor on dark shell. */}
                                <div
                                    className="text-lg font-bold leading-tight mb-1"
                                    style={{
                                        color: '#7ec2ce',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {ps.nameAr}
                                </div>
                                {ps.brandAr && (
                                    <div className="text-[11px] mb-2" style={{ color: 'rgba(255,255,255,0.55)' }}>
                                        {ps.brandAr}
                                    </div>
                                )}
                                {ps.price != null && ps.price > 0 && (
                                    <div
                                        className="text-2xl font-bold mb-3"
                                        style={{
                                            color: '#a8dce4',
                                            textShadow: '0 1px 8px rgba(126,194,206,0.30)',
                                            letterSpacing: '0.01em',
                                        }}
                                    >
                                        {ps.price.toFixed(2)} ريال
                                    </div>
                                )}
                            </div>

                            {bullets.length > 0 && (
                                <div>
                                    <div className="border-t mb-2" style={{ borderColor: 'rgba(255,255,255,0.10)' }} />
                                    <div className="text-[11px] font-bold mb-1" style={{ color: '#7ec2ce' }}>
                                        ليش الأفضل:
                                    </div>
                                    <ul className="space-y-1 m-0 p-0 list-none">
                                        {bullets.map((b, i) => (
                                            <li
                                                key={i}
                                                className="text-[12px] leading-snug flex gap-1.5"
                                                style={{ color: 'rgba(255,255,255,0.82)' }}
                                            >
                                                <span aria-hidden style={{ color: '#7ec2ce', flexShrink: 0 }}>•</span>
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Single compact trust pill at the bottom — replaces the
                                duplicated TrustBadge in header + the inline "أصلي 100%"
                                line in the body. Stays clickable via TrustBadge's own
                                modal trigger; the wrapping <span> swallows the Link
                                navigation so tapping the pill opens the certificate
                                modal instead of jumping to the product page. */}
                            <div
                                className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold self-start"
                                style={{
                                    background: 'rgba(126,194,206,0.10)',
                                    border: '1px solid rgba(126,194,206,0.30)',
                                    color: '#a8dce4',
                                }}
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            >
                                <span aria-hidden>🔒</span>
                                <span>أصلي 100%</span>
                                <span aria-hidden style={{ opacity: 0.50 }}>·</span>
                                <span>مورد معتمد</span>
                                <span aria-hidden style={{ opacity: 0.50 }}>·</span>
                                <TrustBadge compact />
                            </div>
                        </div>
                    </div>
                </Link>
            </div>
        </>
    );
}
